import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrosshairs,
  faCheck,
  faArrowRight,
  faSpinner,
  faArrowLeft,
  faBullhorn,
  faUsers,
  faLayerGroup,
  faClock,
  faShieldAlt,
  faPaperPlane
} from "@fortawesome/free-solid-svg-icons";
import { getToken } from '../../function/token.jsx';
import domain from "../../../reusecomponent/domain.jsx";
import { ErrorToastContainer, fetchTemplateDetails, SuccessToastContainer, WorningToastContainer } from '../../function/CustomFunction';
import Templatelist from "./templatelist.jsx";
import TemplatePreview from "./TemplatePreview.jsx";
import UploadModal from "../../../reusecomponent/UploadModal.jsx";
import './managecampaigns.css';

function RetargetingCampaign() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const messageRetargetingService = useSelector((state) => state?.serviceStore?.value?.message_retargeting);
  const isRetargetingEnabled = Boolean(
    messageRetargetingService && 
    messageRetargetingService !== "0" && 
    messageRetargetingService !== 0
  );

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const rawSourceId = queryParams.get("sourceId");

  // Safely decode Base64 sourceId from query string
  const decodedSourceId = useMemo(() => {
    if (!rawSourceId) return "";
    try {
      return atob(rawSourceId);
    } catch (e) {
      return rawSourceId;
    }
  }, [rawSourceId]);

  const [currentStep, setCurrentStep] = useState(1);
  const [retargetName, setRetargetName] = useState("");
  const [pastCampaigns, setPastCampaigns] = useState([]);
  const [selectedSourceCampaignId, setSelectedSourceCampaignId] = useState(decodedSourceId || campaignId || "");
  const [statusFilter, setStatusFilter] = useState("read"); // read, delivered, failed131049, all
  
  const [retargetContacts, setRetargetContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);
  const [selectAll, setSelectAll] = useState(true);

  // Template selection and mapping state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [file, setFile] = useState(null);
  const fileUrl = useRef("");
  const fileType = useRef(null);

  // Schedule & Send state
  const [isSending, setIsSending] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [isPolicyConfirmed, setIsPolicyConfirmed] = useState(false);

  const [scheduleDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [scheduleTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`;
  });

  const initialVariableValue = useMemo(() => ({
    value_type: "",
    value: null,
    content_type: null
  }), []);

  // Sync decodedSourceId with selectedSourceCampaignId
  useEffect(() => {
    if (decodedSourceId) {
      setSelectedSourceCampaignId(String(decodedSourceId));
    } else if (campaignId) {
      setSelectedSourceCampaignId(String(campaignId));
    }
  }, [decodedSourceId, campaignId]);

  // Fetch all past campaigns for reference
  useEffect(() => {
    const fetchPastCampaigns = async () => {
      const token = getToken();
      if (!token) return;
      try {
        const response = await fetch(`${domain()}/campaign`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (data.status && data.data?.data) {
          setPastCampaigns(data.data.data);
        }
      } catch (err) {
        console.error("Error fetching campaigns for retargeting:", err);
      }
    };
    fetchPastCampaigns();
  }, []);

  // Selected source campaign object
  const selectedCampaignObj = useMemo(() => {
    return pastCampaigns.find(c => String(c.id) === String(selectedSourceCampaignId));
  }, [pastCampaigns, selectedSourceCampaignId]);

  // Auto-generate default retargeting campaign name when campaign is selected
  useEffect(() => {
    if (selectedCampaignObj && (!retargetName || retargetName.startsWith('Retarget_'))) {
      setRetargetName(`Retarget_${selectedCampaignObj.campaign_name}`);
    }
  }, [selectedCampaignObj, retargetName]);

  // Fetch recipients for selected source campaign and filter by status
  const fetchSourceRecipients = useCallback(async (sourceId, filter) => {
    if (!sourceId) return;
    const token = getToken();
    if (!token) return;

    // Map filter selection to target string: 'read', 'delivered', 'sent', 'failed131049', or 'all'
    let targetStr = "all";
    const filterLower = String(filter).toLowerCase();
    if (filterLower.includes("read")) targetStr = "read";
    else if (filterLower.includes("delivered")) targetStr = "delivered";
    else if (filterLower.includes("sent")) targetStr = "sent";
    else if (filterLower.includes("failed")) targetStr = "failed131049";
    else targetStr = "all";

    setLoadingContacts(true);
    setNextPageUrl(null);
    setRetargetContacts([]);
    setTotalContacts(0);

    try {
      const encodedCampaignId = btoa(String(sourceId));
      const encodedTarget = btoa(targetStr);
      const apiUrl = `${domain()}/campaign/filter/${encodedCampaignId}/${encodedTarget}?page=1`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      let rawList = [];
      let nextUrl = null;

      if (data.status && data.data) {
        if (Array.isArray(data.data.data)) {
          rawList = data.data.data;
        } else if (Array.isArray(data.data)) {
          rawList = data.data;
        } else if (Array.isArray(data.result)) {
          rawList = data.result;
        }
        nextUrl = data.data.next_page_url || null;
        if (data.data.total !== undefined && data.data.total !== null) {
          setTotalContacts(data.data.total);
        } else if (data.total !== undefined && data.total !== null) {
          setTotalContacts(data.total);
        }
      }

      setNextPageUrl(nextUrl);

      if (rawList.length > 0) {
        const mapped = rawList.map((c, idx) => ({
          ...c,
          id: c.id || c.contact_id || idx + 1,
          contact_id: c.contact_id || c.id || idx + 1,
          name: c.name || c.contact_name || c.recipient_name || 'Recipient',
          mobile: c.phone_number || c.phone || c.mobile || '',
          status: c.status || targetStr.toUpperCase(),
          selected: true
        }));

        setRetargetContacts(mapped);
        setSelectAll(true);
      } else {
        setRetargetContacts([]);
        setTotalContacts(0);
      }
    } catch (err) {
      console.error("Error fetching filtered campaign recipients:", err);
      ErrorToastContainer("Failed to load source campaign contacts");
      setRetargetContacts([]);
      setTotalContacts(0);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // Infinite Scroll: Fetch next page of contacts when scrolled down
  const fetchNextPage = useCallback(async () => {
    if (!nextPageUrl || loadingMore || loadingContacts) return;
    const token = getToken();
    if (!token) return;

    setLoadingMore(true);
    try {
      let fetchUrl = nextPageUrl;
      if (fetchUrl.includes('/campaign/filter/')) {
        const pathWithQuery = fetchUrl.substring(fetchUrl.indexOf('/campaign/filter/'));
        fetchUrl = `${domain()}${pathWithQuery}`;
      } else if (fetchUrl.startsWith('http://') && window.location.protocol === 'https:') {
        fetchUrl = fetchUrl.replace('http://', 'https://');
      }

      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      let rawList = [];
      let nextUrl = null;

      if (data.status && data.data) {
        if (Array.isArray(data.data.data)) {
          rawList = data.data.data;
        } else if (Array.isArray(data.data)) {
          rawList = data.data;
        }
        nextUrl = data.data.next_page_url || null;
        if (data.data.total !== undefined && data.data.total !== null) {
          setTotalContacts(data.data.total);
        }
      }

      setNextPageUrl(nextUrl);

      if (rawList.length > 0) {
        const mapped = rawList.map((c, idx) => ({
          ...c,
          id: c.id || c.contact_id || `next_${Date.now()}_${idx}`,
          contact_id: c.contact_id || c.id || idx + 1,
          name: c.name || c.contact_name || c.recipient_name || 'Recipient',
          mobile: c.phone_number || c.phone || c.mobile || '',
          status: c.status || statusFilter.toUpperCase(),
          selected: selectAll
        }));

        setRetargetContacts(prev => {
          const existingMobiles = new Set(prev.map(item => item.mobile));
          const filteredNew = mapped.filter(item => !existingMobiles.has(item.mobile));
          return [...prev, ...filteredNew];
        });
      }
    } catch (err) {
      console.error("Error fetching next page of contacts:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [nextPageUrl, loadingMore, loadingContacts, statusFilter, selectAll]);

  // Handle scroll event on contacts table wrapper
  const handleTableScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 40 && nextPageUrl && !loadingMore && !loadingContacts) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (selectedSourceCampaignId) {
      fetchSourceRecipients(selectedSourceCampaignId, statusFilter);
    }
  }, [selectedSourceCampaignId, statusFilter, fetchSourceRecipients]);

  // Fetch available WhatsApp templates
  const fetchTemplates = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoadingTemplates(true);
    try {
      const response = await fetch(`${domain()}/manage-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.status) {
        const approved = (data?.data || []).filter(
          item => item.status === "APPROVED" && (item.category === 'MARKETING' || item.category === 'UTILITY')
        );
        setTemplates(approved);
      }
    } catch (err) {
      ErrorToastContainer("Error fetching templates");
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    if (currentStep === 3 && templates.length === 0) {
      fetchTemplates();
    }
  }, [currentStep, templates.length, fetchTemplates]);

  // Handle template selection
  const handleTemplateSelection = async (template) => {
    if (selectedTemplate != null && selectedTemplate.id === template.id) return;
    const detailedTemplate = await fetchTemplateDetails(template.id);
    if (detailedTemplate && detailedTemplate.status) {
      const detailedData = detailedTemplate.data;
      setSelectedTemplate(detailedData);
      let newVars = {};

      if (detailedData.components) {
        try {
          const components = Array.isArray(detailedData.components)
            ? detailedData.components
            : JSON.parse(detailedData.components);

          components.forEach((component) => {
            if (component.type === "HEADER") {
              fileType.current = null;
              if (component.format === 'TEXT' && component.text) {
                const matches = component.text.match(/\{\{(\d+)\}\}/g) || [];
                matches.forEach(() => { newVars['header'] = { ...initialVariableValue }; });
              } else if (['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO'].includes(component.format)) {
                fileType.current = component.format;
                newVars['header'] = { ...initialVariableValue, content_type: component.format };
              } else if (component.format === 'LOCATION') {
                WorningToastContainer("Location templates not supported for retargeting");
              }
            } else if (component.type === "BODY" && component.text) {
              const matches = component.text.match(/\{\{(\d+)\}\}/g) || [];
              matches.forEach((m) => {
                const idx = m.replace(/[^\d]/g, "");
                newVars[`body_${idx}`] = { ...initialVariableValue };
              });
            }
          });
        } catch (e) {
          console.error('Error parsing components JSON:', e);
        }
      }
      setTemplateVariables(newVars);
    } else {
      setSelectedTemplate(template);
    }
  };

  const handleVariableMapping = (variable, valueType, mappedValue, content_type) => {
    const isHeaderVar = variable.startsWith('header');
    const resolvedContentType = isHeaderVar
      ? (content_type !== undefined ? content_type : (fileType.current === null ? 'TEXT' : fileType.current))
      : 'TEXT';

    setTemplateVariables(prev => ({
      ...prev,
      [variable]: {
        value_type: valueType,
        value: mappedValue !== undefined ? mappedValue : (prev[variable]?.value || null),
        content_type: resolvedContentType
      }
    }));
  };

  const FilePathUpdate = (path) => {
    if (path) {
      let url = `${domain()}/whatsapp/campaign/files/${path}`;
      fileUrl.current = url;
      handleVariableMapping('header', 'STATIC', url, fileType.current === null ? "TEXT" : fileType.current);
    }
    setFile(null);
  };

  const handleSelectAll = () => {
    const next = !selectAll;
    setSelectAll(next);
    setRetargetContacts(prev => prev.map(c => ({ ...c, selected: next })));
  };

  const handleSelectContact = (id) => {
    setRetargetContacts(prev => {
      const updated = prev.map(c => c.id === id ? { ...c, selected: !c.selected } : c);
      setSelectAll(updated.every(c => c.selected));
      return updated;
    });
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!selectedSourceCampaignId) {
        WorningToastContainer("Please select a source campaign to retarget");
        return;
      }
      if (!retargetName.trim()) {
        setRetargetName(`Retarget_${new Date().toLocaleDateString()}`);
      }
    } else if (currentStep === 2) {
      if (retargetContacts.filter(c => c.selected).length === 0) {
        WorningToastContainer("Please select at least one recipient");
        return;
      }
    } else if (currentStep === 3) {
      if (!selectedTemplate) {
        WorningToastContainer("Please select a template");
        return;
      }

      for (const key in templateVariables) {
        const { value_type, value, content_type } = templateVariables[key];
        if (!["STATIC", "DYNAMIC"].includes(value_type)) {
          WorningToastContainer(`Please select value type for ${key}`);
          return;
        }

        if (key === 'header' && ['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO'].includes(content_type) && value_type === 'STATIC') {
          try {
            new URL(value);
          } catch (e) {
            WorningToastContainer(`Please Select Valid ${content_type} Url`);
            return;
          }
        }

        if ((value === null || value === '') && value_type === 'STATIC') {
          WorningToastContainer(`Please enter value for ${key}`);
          return;
        }

        if ((value === null || value === '') && value_type === 'DYNAMIC') {
          WorningToastContainer(`Please select contact field for ${key}`);
          return;
        }
      }
    }

    setCurrentStep(prev => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Launch Retargeting Campaign
  const sendRetargeting = async (mode) => {
    if (!isRetargetingEnabled) {
      ErrorToastContainer("Your message retargeting feature is not enabled");
      return;
    }

    const selectedList = retargetContacts.filter(c => c.selected);
    if (mode === 'Production' && selectedList.length === 0) {
      ErrorToastContainer("Please select at least one contact to retarget.");
      return;
    }

    if (mode === 'Production' && !isPolicyConfirmed) {
      ErrorToastContainer("Please confirm message policy compliance.");
      return;
    }

    if (mode === 'Test') {
      if (!testPhoneNumber || testPhoneNumber.trim() === '') {
        WorningToastContainer("Enter a valid test phone number");
        return;
      }
      setIsSendingTest(true);
    } else {
      setIsSending(true);
    }

    try {
      const token = getToken();
      const endpoint = mode === 'Test' ? `${domain()}/campaign_test` : `${domain()}/campaign_by_retargeting`;
      
      const numericCampaignId = parseInt(selectedSourceCampaignId, 10);
      const numericTemplateId = parseInt(selectedTemplate.id, 10);

      const firstTargetContact = selectedList.length > 0 ? selectedList[0] : (retargetContacts.length > 0 ? retargetContacts[0] : null);
      let retargetingFirstContactList = "";
      if (firstTargetContact) {
        const firstContact = {
          name: firstTargetContact.name || '',
          phone_number: firstTargetContact.mobile || firstTargetContact.phone_number || ''
        };
        retargetingFirstContactList = JSON.stringify(firstContact);
      }

      const formData = new FormData();
      formData.append('campaign_type', 'RETARGETING');
      formData.append('template_id', (isNaN(numericTemplateId) ? selectedTemplate.id : numericTemplateId).toString());
      formData.append('templateVariables', JSON.stringify(templateVariables));

      if (retargetingFirstContactList) {
        formData.append('retargeting_first_contact_list', retargetingFirstContactList);
      }

      if (mode === 'Test') {
        formData.append('phonenumber', testPhoneNumber);
      } else {
        formData.append('campaign_name', retargetName || "");
        formData.append('campaign_id', (isNaN(numericCampaignId) ? selectedSourceCampaignId : numericCampaignId).toString());
        formData.append('source_campaign_id', (isNaN(numericCampaignId) ? selectedSourceCampaignId : numericCampaignId).toString());
        formData.append('target_status', statusFilter);
        formData.append('total_number', (totalContacts || retargetContacts.length).toString());
        formData.append('campaign_date', scheduleDate);
        formData.append('campaign_time', scheduleTime);
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const res = await response.json();
      if (res.status) {
        SuccessToastContainer(mode === 'Test' ? "Test retargeting sent!" : "Retargeting campaign scheduled!");
        if (mode === 'Production') {
          navigate('/client-area/whatsapp/manage-campaigns');
        }
      } else {
        ErrorToastContainer(res.message || "Failed to launch retargeting campaign");
      }
    } catch (err) {
      ErrorToastContainer("Server error launching retargeting");
    } finally {
      setIsSending(false);
      setIsSendingTest(false);
    }
  };

  const steps = [
    { id: 1, name: "Select Source Campaign" },
    { id: 2, name: "Filter Recipients" },
    { id: 3, name: "Create Message" },
    { id: 4, name: "Send Test Message" },
    { id: 5, name: "Review & Launch" },
  ];

  const isPreselectedFromAction = Boolean(rawSourceId || campaignId);

  return (
    <div className="main-panel w-100" style={{ minHeight: '85vh' }}>
      <div className="content-wrapper">
        <div className="card border-0 shadow-sm rounded-4">
          <div className="card-body p-4">
            
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div className="p-3 bg-success bg-opacity-10 text-success rounded-circle">
                  <FontAwesomeIcon icon={faCrosshairs} size="xl" />
                </div>
                <div>
                  <h4 className="fw-bold text-dark mb-0">Retargeting Campaign</h4>
                  <p className="text-muted small mb-0">Re-engage past audience members based on their message engagement history.</p>
                </div>
              </div>
              <button className="btn btn-outline-secondary btn-sm rounded-pill" onClick={() => navigate('/client-area/whatsapp/manage-campaigns')}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
              </button>
            </div>

            {/* Stepper */}
            <div className="mb-3 mb-md-4 campaign-stepper-container">
              <div className="d-flex justify-content-between position-relative">
                <div 
                  className="position-absolute"
                  style={{ height: "3px", backgroundColor: "#e2e8f0", width: "100%", top: "14px", zIndex: 0 }}
                ></div>
                <div 
                  className="position-absolute"
                  style={{
                    height: "3px",
                    backgroundColor: "#075E54",
                    width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
                    top: "14px",
                    zIndex: 0,
                    transition: "width 0.3s ease-in-out",
                  }}
                ></div>

                {steps.map((step) => (
                  <div key={step.id} className="text-center position-relative campaign-step-item" style={{ flex: 1, zIndex: 1 }}>
                    <div 
                      className={`rounded-circle d-flex align-items-center justify-content-center mb-1 mx-auto fw-bold campaign-step-circle ${
                        currentStep > step.id ? "bg-success text-white" : currentStep === step.id ? "text-white" : "bg-light text-muted border"
                      }`}
                      style={{ width: "30px", height: "30px", backgroundColor: currentStep === step.id ? '#075E54' : undefined, fontSize: '12px' }}
                    >
                      {currentStep > step.id ? <FontAwesomeIcon icon={faCheck} /> : step.id}
                    </div>
                    <div className={`campaign-step-title small text-truncate ${currentStep === step.id ? "text-dark fw-bold active-step-title" : "text-muted"}`}>
                      {step.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step 1: Select Source Campaign & Status */}
            {currentStep === 1 && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="mb-3">
                    <span className="badge px-2.5 py-1 rounded-pill mb-1" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.72rem)' }}>
                      STEP 1 • SOURCE CAMPAIGN
                    </span>
                    <h4 className="fw-bold mb-1 text-dark fs-5">Choose Source Campaign to Retarget</h4>
                    <p className="text-muted mb-0 small">Select a previously broadcasted campaign and target specific recipient statuses.</p>
                  </div>
                  
                  <div className="form-group mb-3">
                    <label className="form-label fw-semibold small mb-1">Retargeting Campaign Name *</label>
                    <input
                      type="text"
                      className="form-control rounded-3"
                      placeholder="e.g. Retarget_Festival_Read_Users"
                      value={retargetName}
                      onChange={(e) => setRetargetName(e.target.value)}
                    />
                  </div>

                  <div className="row g-3 mb-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small mb-1">Target Source Campaign *</label>
                      {isPreselectedFromAction ? (
                        <input
                          type="text"
                          className="form-control bg-light fw-bold text-dark"
                          value={selectedCampaignObj ? `${selectedCampaignObj.campaign_name}` : `Campaign ID: ${selectedSourceCampaignId}`}
                          disabled
                          readOnly
                        />
                      ) : (
                        <select
                          className="form-select"
                          value={selectedSourceCampaignId}
                          onChange={(e) => setSelectedSourceCampaignId(e.target.value)}
                        >
                          <option value="">-- Choose Campaign --</option>
                          {pastCampaigns.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.campaign_name} ({new Date(c.created_at).toLocaleDateString()})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold small mb-1">Target Recipient Engagement Status *</label>
                      <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="read">Read / Opened Message (Engaged Users)</option>
                        <option value="delivered">Delivered (Unread)</option>
                        <option value="sent">Sent Message</option>
                        <option value="failed131049">Failed (131049)</option>
                        <option value="all">All Past Recipients</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-end align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-1.5 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep} 
                    disabled={!selectedSourceCampaignId}
                  >
                    Next <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Review Filtered Contacts */}
            {currentStep === 2 && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body d-flex flex-column">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 pb-3 mb-3 border-bottom">
                    <div>
                      <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                        STEP 2 • FILTERED RECIPIENTS
                      </span>
                      <h4 className="fw-bold mb-1 text-dark">
                        Target Audience Contacts ({loadingContacts ? '...' : (totalContacts > 0 ? totalContacts : retargetContacts.length)})
                      </h4>
                      <p className="text-muted small mb-0">Showing contacts filtered by: <strong>{statusFilter === 'FAILED' ? 'Failed (131049)' : statusFilter}</strong></p>
                    </div>
                  </div>

                  {loadingContacts ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-success mb-2" />
                      <p className="text-muted">Filtering contacts...</p>
                    </div>
                  ) : (
                    <div 
                      className="audience-table-wrapper mb-3" 
                      style={{ flex: '1 1 auto', minHeight: '260px', overflowY: "auto" }}
                      onScroll={handleTableScroll}
                    >
                      <table className="table audience-table table-hover align-middle mb-0 text-center">
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                          <tr>
                            <th>Recipient Name</th>
                            <th>Phone Number</th>
                            <th>Past Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {retargetContacts.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="py-4 text-muted">No contacts found for this status filter.</td>
                            </tr>
                          ) : (
                            retargetContacts.map(c => (
                              <tr key={c.id}>
                                <td className="fw-medium">{c.name}</td>
                                <td>{c.mobile}</td>
                                <td><span className="badge bg-info bg-opacity-10 text-info">{c.status}</span></td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      {loadingMore ? (
                        <div className="text-center py-2 bg-light border-top">
                          <FontAwesomeIcon icon={faSpinner} spin className="text-success me-2" />
                          <span className="small text-muted fw-semibold">Loading more contacts...</span>
                        </div>
                      ) : nextPageUrl ? (
                        <div className="text-center py-2 bg-light border-top">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-success text-decoration-none fw-semibold p-0"
                            onClick={() => fetchNextPage()}
                          >
                            + Load More Contacts ({retargetContacts.length} loaded)
                          </button>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep} 
                    disabled={retargetContacts.length === 0}
                  >
                    Next Step
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Select & Map Template */}
            {currentStep === 3 && (
              <div className="campaign-step-card w-100">
                {file != null && (
                  <UploadModal FilePathUpdate={FilePathUpdate} file={file} url={`${domain()}/whatsapp/campaign/uplode-file`}/>
                )}
                
                <div className="campaign-step-scroll-body">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3 pb-2 border-bottom">
                    <div>
                      <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                        STEP 3 • TEMPLATE
                      </span>
                      <h4 className="fw-bold mb-1 text-dark">Select Template for Retargeting</h4>
                      <p className="text-muted mb-0 small">Choose an approved WhatsApp template and map parameters.</p>
                    </div>
                  </div>
                  
                  {loadingTemplates ? (
                    <div className="text-center py-5">
                      <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-success mb-2" />
                      <p className="text-muted">Loading approved templates...</p>
                    </div>
                  ) : (
                    <div className="row g-3 mb-3">
                      {templates.map((t) => (
                        <Templatelist 
                          key={t.id} 
                          template={t} 
                          selectedTemplate={selectedTemplate} 
                          handleTemplateSelectionFunction={handleTemplateSelection} 
                        />
                      ))}
                    </div>
                  )}

                  {/* Variable Mapping */}
                  {Object.keys(templateVariables).length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                      <h5 className="fw-semibold text-dark">Map Template Variables</h5>
                      <p className="text-muted mb-3 small">Map template parameters to recipient fields or static values.</p>
                      
                      <div className="row g-3 m-0 p-0">
                        {Object.keys(templateVariables).map((vKey, idx) => {
                          const isHeaderVar = vKey.startsWith('header');
                          const activeContentType = fileType.current || templateVariables[vKey]?.content_type;
                          const isMediaHeader = isHeaderVar && ['IMAGE', 'VIDEO', 'DOCUMENT', 'AUDIO'].includes(activeContentType);
                          const isStatic = templateVariables[vKey]?.value_type === 'STATIC';
                          const isDynamic = templateVariables[vKey]?.value_type === 'DYNAMIC';

                          return (
                            <div key={vKey} className="col-12 p-0 m-0 my-1">
                              <div className="card-body card m-0 p-0 px-2 border rounded-3">
                                <div className="row align-items-center align-content-center justify-content-between m-0 py-2">
                                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                                    <label className="form-label fw-semibold mb-0">
                                      {isHeaderVar ? "Header" : vKey.startsWith('body_') ? `Body Variable ${vKey.split('_')[1]}` : `Variable ${idx + 1}`}:
                                    </label>
                                  </div>
                                  <div className="col-12 col-md-3 mb-2 mb-md-0">
                                    <label className="form-label small mb-1">Select Value Type</label>
                                    <select
                                      className="form-select form-select-sm"
                                      value={templateVariables[vKey]?.value_type || ""}
                                      onChange={(e) => handleVariableMapping(vKey, e.target.value)}
                                    >
                                      <option value="">Select Value Type</option>
                                      <option value="STATIC">Static</option>
                                      <option value="DYNAMIC">Dynamic</option>
                                    </select>
                                  </div>

                                  {/* Header Media File Upload & File URL Box */}
                                  {isStatic && isMediaHeader && (
                                    <>
                                      <div className="col-12 col-md-3 mb-2 mb-md-0">
                                        <label className="form-label small mb-1">{`Upload ${activeContentType}`}</label>
                                        <input
                                          type="file"
                                          className="form-control form-control-sm pt-1 pl-1"
                                          accept={
                                            activeContentType === "IMAGE" ? "image/*" :
                                            activeContentType === "VIDEO" ? "video/*" :
                                            activeContentType === "AUDIO" ? "audio/*" :
                                            activeContentType === "DOCUMENT" ? "*/*" : ""
                                          }
                                          onChange={(e) => setFile(e.target.files[0])}
                                        />
                                      </div>
                                      <div className="col-12 col-md-3">
                                        <label className="form-label small mb-1">File URL:</label>
                                        <input
                                          type="url"
                                          readOnly
                                          className="form-control form-control-sm"
                                          placeholder="File URL will appear here"
                                          value={templateVariables[vKey]?.value || fileUrl.current || ""}
                                        />
                                      </div>
                                    </>
                                  )}

                                  {/* Text Input for STATIC Body */}
                                  {isStatic && !isMediaHeader && (
                                    <div className="col-12 col-md-4">
                                      <label className="form-label small mb-1">Enter Static Value</label>
                                      <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder="Enter static value"
                                        value={templateVariables[vKey]?.value || ""}
                                        onChange={(e) => handleVariableMapping(vKey, 'STATIC', e.target.value, isHeaderVar ? (activeContentType || 'TEXT') : 'TEXT')}
                                      />
                                    </div>
                                  )}

                                  {/* Dropdown for DYNAMIC Mapping */}
                                  {isDynamic && (
                                    <div className="col-12 col-md-4">
                                      <label className="form-label small mb-1">Select Contact Field</label>
                                      <select
                                        className="form-select form-select-sm"
                                        value={templateVariables[vKey]?.value || ""}
                                        onChange={(e) => handleVariableMapping(vKey, 'DYNAMIC', e.target.value, isHeaderVar ? (activeContentType || 'TEXT') : 'TEXT')}
                                      >
                                        <option value="">Select contact field</option>
                                        <option value="name">Name</option>
                                        <option value="mobile">Mobile Number</option>
                                      </select>
                                    </div>
                                  )}

                                  {!isStatic && !isDynamic && (
                                    <div className="col-12 col-md-4"></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep} 
                    disabled={!selectedTemplate}
                  >
                    Next Step
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Send Test Message */}
            {currentStep === 4 && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="row g-3 g-lg-4">
                    <div className="col-12 col-lg-6">
                      <div className="card border-0 shadow-sm rounded-4 p-4 h-100 d-flex flex-column justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div>
                          <span className="badge px-3 py-1 rounded-pill mb-2 align-self-start" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)' }}>
                            STEP 4 • QUALITY CHECK
                          </span>
                          <h4 className="fw-bold mb-2">Send Test Message</h4>
                          <p className="text-muted mb-4 small">Send a sample message to your phone before launching.</p>

                          <div className="mb-4">
                            <label className="form-label fw-semibold">Phone Number (with country code) *</label>
                            <input
                              type="text"
                              className="form-control form-control-lg rounded-3"
                              placeholder="e.g. 919876543210"
                              value={testPhoneNumber}
                              onChange={(e) => setTestPhoneNumber(e.target.value)}
                            />
                            <div className="form-text mt-2 text-muted small">
                              Include country code without + (e.g. 91 for India).
                            </div>
                          </div>
                        </div>

                        <div>
                          <button
                            type="button"
                            className="btn btn-campaign-primary w-100 py-2.5 rounded-pill fw-semibold shadow-sm"
                            onClick={() => sendRetargeting('Test')}
                            disabled={isSendingTest}
                          >
                            <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                            {isSendingTest ? 'Sending Test...' : 'Send Test WhatsApp Message'}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-lg-6 d-flex justify-content-center">
                      <TemplatePreview
                        templateVariables={templateVariables}
                        selectedTemplate={selectedTemplate}
                        sampleData={retargetContacts[0]}
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-campaign-primary px-4 px-md-5 py-2 rounded-pill fw-semibold shadow" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handleNextStep}
                  >
                    Continue to Summary
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Review & Launch */}
            {currentStep === 5 && (
              <div className="campaign-step-card w-100">
                <div className="campaign-step-scroll-body">
                  <div className="row g-3 g-lg-4">
                    <div className="col-12 col-lg-6">
                      <div className="card border-0 shadow-sm rounded-4 p-4 h-100 d-flex flex-column justify-content-between" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div>
                          {/* Header Badge & Title */}
                          <div className="mb-3">
                            <span className="badge px-3 py-1 rounded-pill mb-2 align-self-start" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)', fontWeight: 700, fontSize: 'var(--font-size-xs, 0.75rem)', letterSpacing: '0.5px' }}>
                              FINAL STEP • CONFIRM &amp; SEND
                            </span>
                            <h4 className="fw-bold mb-1 text-dark">Retargeting Summary</h4>
                            <p className="text-muted small mb-0">Review your audience and retargeting parameters before launching.</p>
                          </div>

                          {/* Summary KPI Cards Grid (2x2) */}
                          <div className="row g-2 mb-3">
                            {/* Card 1: Retargeting Name */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: 'rgba(7,94,84,0.1)', color: 'var(--companycolor, #075E54)' }}>
                                  <FontAwesomeIcon icon={faBullhorn} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Campaign Name</div>
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={retargetName}>
                                    {retargetName || 'Untitled'}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 2: Target Status Filter */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#eff6ff', color: '#2563eb' }}>
                                  <FontAwesomeIcon icon={faCrosshairs} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Target Filter</div>
                                  <div className="fw-bold text-primary text-truncate text-capitalize" style={{ fontSize: '0.875rem' }}>
                                    {statusFilter === 'FAILED' ? 'Failed (131049)' : statusFilter}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 3: Selected Recipients */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#dcfce7', color: '#16a34a' }}>
                                  <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Recipients Count</div>
                                  <div className="fw-bold text-success text-truncate" style={{ fontSize: '0.875rem' }}>
                                    {retargetContacts.filter(c => c.selected).length} contacts
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Card 4: Selected Template */}
                            <div className="col-12 col-sm-6">
                              <div className="summary-kpi-card">
                                <div className="summary-kpi-icon" style={{ background: '#fef3c7', color: '#d97706' }}>
                                  <FontAwesomeIcon icon={faLayerGroup} />
                                </div>
                                <div className="summary-kpi-content">
                                  <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: 600 }}>Selected Template</div>
                                  <div className="fw-bold text-dark text-truncate" style={{ fontSize: '0.875rem' }} title={selectedTemplate?.name || selectedTemplate?.template_name}>
                                    {selectedTemplate?.name || selectedTemplate?.template_name || '—'}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* WhatsApp Policy Confirmation Interactive Card */}
                          <div 
                            className={`compliance-agreement-card mb-3 ${isPolicyConfirmed ? 'is-confirmed' : ''}`}
                            onClick={() => setIsPolicyConfirmed(!isPolicyConfirmed)}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                                <input
                                  type="checkbox"
                                  className="compliance-checkbox"
                                  id="retargetConfirm"
                                  checked={isPolicyConfirmed}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setIsPolicyConfirmed(e.target.checked);
                                  }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.845rem' }}>
                                  <FontAwesomeIcon icon={faShieldAlt} style={{ color: 'var(--companycolor, #075E54)' }} />
                                  <span>WhatsApp Business Policy Compliance</span>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.75rem', lineHeight: 1.35 }}>
                                  I confirm that this retargeting broadcast complies with Meta WhatsApp messaging policies.
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Launch Button */}
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-campaign-primary w-100 py-3 rounded-pill fw-bold text-uppercase shadow d-flex align-items-center justify-content-center gap-2"
                            style={{
                              fontSize: '0.925rem',
                              letterSpacing: '0.5px'
                            }}
                            onClick={() => sendRetargeting('Production')}
                            disabled={isSending || isSendingTest || !isPolicyConfirmed}
                          >
                            {isSending ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Launching Retargeting...</span>
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faPaperPlane} />
                                <span>Launch Retargeting Broadcast</span>
                              </>
                            )}
                          </button>
                        </div>

                      </div>
                    </div>

                    <div className="col-12 col-lg-6 d-flex justify-content-center">
                      <TemplatePreview
                        templateVariables={templateVariables}
                        selectedTemplate={selectedTemplate}
                        sampleData={retargetContacts[0]}
                      />
                    </div>
                  </div>
                </div>

                <div className="campaign-step-footer d-flex justify-content-between align-items-center">
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary px-4 py-2 rounded-pill fw-semibold" 
                    style={{ fontSize: 'var(--font-size-sm, 0.875rem)' }}
                    onClick={handlePrevStep}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                    Previous
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

export default RetargetingCampaign;
